import axios from "axios";

// Keep exports to prevent build breakages if imported elsewhere
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND_URL}/api`;

const CONFIG = {
  fx_usd_vnd: 26000,
  min_withdraw_vnd: 50000,
  fee_pct: 0.005,
  flat_fee_vnd: 10000,
  flat_fee_usd: 0.40,
};

const USERS_KEY = "pay10x.users";
const LEDGER_KEY = "pay10x.ledger";
const DESTINATIONS_KEY = "pay10x.destinations";
const PAYOUTS_KEY = "pay10x.payouts";
const PROFILES_KEY = "pay10x.profiles";

// Helper functions for storage
function getStorage(key, def = []) {
  const data = localStorage.getItem(key);
  return data ? JSON.parse(data) : def;
}

function setStorage(key, data) {
  localStorage.setItem(key, JSON.stringify(data));
}

function uuid() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function(c) {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function nowIso() {
  return new Date().toISOString();
}

// Generate structured Axios-like errors to fit the frontend toast handling
function makeAxiosError(message) {
  const error = new Error(message);
  error.response = {
    data: {
      detail: message,
    },
  };
  return error;
}

// Seed function for starting demo data
function seedDatabase() {
  if (localStorage.getItem(USERS_KEY)) {
    return;
  }

  const users = [
    { id: "user-1", name: "Aiko Nguyen (Customer A)", email: "a@demo.dev", role: "customer", created_at: nowIso() },
    { id: "user-2", name: "Bao Tran (Customer B)", email: "b@demo.dev", role: "customer", created_at: nowIso() },
    { id: "user-3", name: "Chi Le (Customer C)", email: "c@demo.dev", role: "customer", created_at: nowIso() },
    { id: "user-4", name: "Ops Admin", email: "admin@demo.dev", role: "admin", created_at: nowIso() }
  ];
  setStorage(USERS_KEY, users);

  const seed_dest_templates = [
    { label: "Vietcombank Primary", currency: "VND", bank_name: "Vietcombank", account_number: "0011-234567-890" },
    { label: "Wise USD", currency: "USD", bank_name: "Wise (US)", account_number: "8493-2211-0055" }
  ];

  const earn_sources = [
    "Content earnings — YouTube Shorts payout",
    "Content earnings — TikTok Creator Fund",
    "Content earnings — Sponsored post",
    "Content earnings — Affiliate revenue share",
    "Content earnings — Newsletter subscribers",
    "Adjustment — Referral bonus"
  ];

  const destinations = [];
  const ledger_entries = [];
  const profiles = [];

  users.forEach(u => {
    if (u.role === "admin") return;

    profiles.push({
      user_id: u.id,
      kyc_tier: "basic",
      country: "VN",
      note: "Initial seeded profile",
      onboarded_at: u.created_at
    });

    seed_dest_templates.forEach(d => {
      destinations.push({
        id: "dest-" + uuid().substring(0, 8),
        user_id: u.id,
        label: d.label,
        currency: d.currency,
        bank_name: d.bank_name,
        account_number: d.account_number,
        account_holder: u.name.split(" (")[0],
        created_at: u.created_at
      });
    });

    const num_credits = Math.floor(Math.random() * 4) + 6; // 6 to 9 credits
    const num_debits = Math.floor(Math.random() * 2) + 1; // 1 to 2 debits
    const entries = [];
    const baseTime = Date.now() - 90 * 24 * 3600 * 1000;

    for (let i = 0; i < num_credits; i++) {
      entries.push({
        type: "earning",
        direction: "credit",
        amount_vnd: (Math.floor(Math.random() * 24) + 2) * 100000, // 200k - 2.5M VND
        description: earn_sources[Math.floor(Math.random() * earn_sources.length)],
        status: "completed",
        daysAgo: Math.floor(Math.random() * 90)
      });
    }

    for (let i = 0; i < num_debits; i++) {
      entries.push({
        type: "withdrawal",
        direction: "debit",
        amount_vnd: (Math.floor(Math.random() * 10) + 3) * 100000, // 300k - 1.2M VND
        description: "Withdrawal — Pay10x·Thunes rail",
        status: "completed",
        daysAgo: Math.floor(Math.random() * 90)
      });
    }

    // Sort: oldest first (higher daysAgo first)
    entries.sort((a, b) => b.daysAgo - a.daysAgo);

    let running = 0;
    entries.forEach(e => {
      const ts = new Date(baseTime + (90 - e.daysAgo) * 24 * 3600 * 1000).toISOString();
      const signed = e.direction === "credit" ? e.amount_vnd : -e.amount_vnd;
      running += signed;
      ledger_entries.push({
        id: "ledg-" + uuid().substring(0, 8),
        user_id: u.id,
        type: e.type,
        direction: e.direction,
        amount_vnd: e.amount_vnd,
        description: e.description,
        status: e.status,
        running_balance_vnd: running,
        created_at: ts,
        payout_request_id: null
      });
    });
  });

  setStorage(DESTINATIONS_KEY, destinations);
  setStorage(LEDGER_KEY, ledger_entries);
  setStorage(PROFILES_KEY, profiles);
  setStorage(PAYOUTS_KEY, []);
}

function getBalance(userId) {
  const ledger = getStorage(LEDGER_KEY);
  const userEntries = ledger.filter(e => e.user_id === userId && (e.status === "completed" || e.status === "pending"));
  let balance = 0;
  userEntries.forEach(e => {
    if (e.direction === "credit") {
      balance += e.amount_vnd;
    } else {
      balance -= e.amount_vnd;
    }
  });
  return balance;
}

function appendLedger(entry) {
  const ledger = getStorage(LEDGER_KEY);
  const bal = getBalance(entry.user_id);
  if (entry.status === "completed" || entry.status === "pending") {
    const signed = entry.direction === "credit" ? entry.amount_vnd : -entry.amount_vnd;
    entry.running_balance_vnd = bal + signed;
  } else {
    entry.running_balance_vnd = bal;
  }
  ledger.push(entry);
  setStorage(LEDGER_KEY, ledger);
  return entry;
}

function computeFeesAndNet(amountVnd, payoutCurrency) {
  const feePctAmount = Math.round(amountVnd * CONFIG.fee_pct);
  let feeVnd;
  let netAmount;
  let fxRate;
  if (payoutCurrency === "VND") {
    feeVnd = feePctAmount + CONFIG.flat_fee_vnd;
    const netVnd = amountVnd - feeVnd;
    netAmount = netVnd;
    fxRate = 1.0;
  } else {
    const flatFeeVndEquiv = Math.round(CONFIG.flat_fee_usd * CONFIG.fx_usd_vnd);
    feeVnd = feePctAmount + flatFeeVndEquiv;
    const netVnd = amountVnd - feeVnd;
    netAmount = Math.round((netVnd / CONFIG.fx_usd_vnd) * 100) / 100;
    fxRate = 1 / CONFIG.fx_usd_vnd;
  }
  return { fee_vnd: feeVnd, net_amount: netAmount, fx_rate: fxRate };
}

const mockLatency = (ms = 150) => new Promise(resolve => setTimeout(resolve, ms));

export const api = {
  users: async () => {
    seedDatabase();
    await mockLatency(100);
    return getStorage(USERS_KEY);
  },

  user: async (id) => {
    seedDatabase();
    await mockLatency(50);
    const users = getStorage(USERS_KEY);
    const u = users.find(u => u.id === id);
    if (!u) throw makeAxiosError("User not found");
    return u;
  },

  wallet: async (uid) => {
    seedDatabase();
    await mockLatency(100);
    const balance = getBalance(uid);
    return {
      user_id: uid,
      balance_vnd: balance,
      balance_usd_equivalent: Math.round((balance / CONFIG.fx_usd_vnd) * 100) / 100,
      fx_rate: CONFIG.fx_usd_vnd,
    };
  },

  ledger: async (uid, type) => {
    seedDatabase();
    await mockLatency(120);
    const ledger = getStorage(LEDGER_KEY);
    let userEntries = ledger.filter(e => e.user_id === uid);
    if (type && type !== "all") {
      userEntries = userEntries.filter(e => e.type === type);
    }
    userEntries.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    return userEntries;
  },

  destinations: async (uid) => {
    seedDatabase();
    await mockLatency(80);
    const destinations = getStorage(DESTINATIONS_KEY);
    return destinations.filter(d => d.user_id === uid);
  },

  addDestination: async (uid, body) => {
    seedDatabase();
    await mockLatency(150);
    const accNumClean = body.account_number.replace(/[-\s]/g, "");
    if (accNumClean.length < 6) {
      throw makeAxiosError("Account number appears invalid (mock validation)");
    }
    const dest = {
      id: "dest-" + uuid().substring(0, 8),
      user_id: uid,
      label: body.label,
      currency: body.currency,
      bank_name: body.bank_name,
      account_number: body.account_number,
      account_holder: body.account_holder,
      created_at: nowIso()
    };
    const destinations = getStorage(DESTINATIONS_KEY);
    destinations.push(dest);
    setStorage(DESTINATIONS_KEY, destinations);
    return dest;
  },

  payouts: async (uid) => {
    seedDatabase();
    await mockLatency(120);
    const payouts = getStorage(PAYOUTS_KEY);
    const userPayouts = payouts.filter(p => p.user_id === uid);
    userPayouts.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    return userPayouts;
  },

  quote: async (body) => {
    seedDatabase();
    await mockLatency(100);
    if (body.amount_vnd < CONFIG.min_withdraw_vnd) {
      throw makeAxiosError(`Minimum withdrawal is ${CONFIG.min_withdraw_vnd.toLocaleString()} VND`);
    }
    const balance = getBalance(body.user_id);
    if (body.amount_vnd > balance) {
      throw makeAxiosError("Insufficient balance");
    }
    const fees = computeFeesAndNet(body.amount_vnd, body.payout_currency);
    return {
      amount_vnd: body.amount_vnd,
      payout_currency: body.payout_currency,
      ...fees,
      estimated_arrival: "2–24 hours (mock)",
    };
  },

  withdraw: async (body) => {
    seedDatabase();
    await mockLatency(150);
    if (body.amount_vnd < CONFIG.min_withdraw_vnd) {
      throw makeAxiosError(`Minimum withdrawal is ${CONFIG.min_withdraw_vnd.toLocaleString()} VND`);
    }
    const balance = getBalance(body.user_id);
    if (body.amount_vnd > balance) {
      throw makeAxiosError("Insufficient balance");
    }

    const destinations = getStorage(DESTINATIONS_KEY);
    const dest = destinations.find(d => d.id === body.destination_id && d.user_id === body.user_id);
    if (!dest) {
      throw makeAxiosError("Destination not found");
    }

    const fees = computeFeesAndNet(body.amount_vnd, body.payout_currency);
    const payout = {
      id: "pay-" + uuid().substring(0, 8),
      user_id: body.user_id,
      amount_vnd: body.amount_vnd,
      payout_currency: body.payout_currency,
      destination_id: body.destination_id,
      destination_snapshot: dest,
      status: "pending_approval",
      ...fees,
      created_at: nowIso(),
      updated_at: nowIso(),
    };

    const payouts = getStorage(PAYOUTS_KEY);
    payouts.push(payout);
    setStorage(PAYOUTS_KEY, payouts);

    const entry = {
      id: "ledg-" + uuid().substring(0, 8),
      user_id: body.user_id,
      type: "withdrawal",
      direction: "debit",
      amount_vnd: body.amount_vnd,
      description: `Withdrawal → ${dest.bank_name} (${body.payout_currency})`,
      status: "pending",
      payout_request_id: payout.id,
      created_at: nowIso(),
    };
    appendLedger(entry);

    return payout;
  },

  payout: async (id) => {
    seedDatabase();
    await mockLatency(80);
    const payouts = getStorage(PAYOUTS_KEY);
    const p = payouts.find(p => p.id === id);
    if (!p) throw makeAxiosError("Payout not found");
    return p;
  },

  advance: async (id, forceFail = false) => {
    seedDatabase();
    await mockLatency(180);
    const payouts = getStorage(PAYOUTS_KEY);
    const pIdx = payouts.findIndex(p => p.id === id);
    if (pIdx === -1) throw makeAxiosError("Payout not found");
    const p = payouts[pIdx];

    if (["completed", "failed", "rejected"].includes(p.status)) {
      return p;
    }

    if (p.status === "pending_approval") {
      throw makeAxiosError("Awaiting admin approval");
    }

    let newStatus = p.status;
    if (forceFail && ["submitted", "processing"].includes(p.status)) {
      newStatus = "failed";
    } else {
      const transitions = {
        approved: "submitted",
        submitted: "processing",
        processing: "completed",
      };
      newStatus = transitions[p.status] || p.status;
    }

    p.status = newStatus;
    p.updated_at = nowIso();

    if (newStatus === "submitted" && !p.rail_reference_id) {
      p.rail_reference_id = "P10X-" + uuid().substring(0, 10).toUpperCase();
      const etaHours = Math.floor(Math.random() * 22) + 2;
      p.eta = new Date(Date.now() + etaHours * 3600 * 1000).toISOString();
    }

    if (newStatus === "completed") {
      const ledger = getStorage(LEDGER_KEY);
      ledger.forEach(entry => {
        if (entry.payout_request_id === p.id && entry.type === "withdrawal") {
          entry.status = "completed";
        }
      });
      setStorage(LEDGER_KEY, ledger);
    } else if (newStatus === "failed") {
      p.failure_reason = "Rail returned FAILED (mock)";
      const ledger = getStorage(LEDGER_KEY);
      ledger.forEach(entry => {
        if (entry.payout_request_id === p.id && entry.type === "withdrawal") {
          entry.status = "completed";
        }
      });
      setStorage(LEDGER_KEY, ledger);

      const reversalEntry = {
        id: "ledg-" + uuid().substring(0, 8),
        user_id: p.user_id,
        type: "reversal",
        direction: "credit",
        amount_vnd: p.amount_vnd,
        description: "Reversal — payout failed (Rail returned FAILED (mock))",
        status: "completed",
        payout_request_id: p.id,
        created_at: nowIso(),
      };
      appendLedger(reversalEntry);
    }

    payouts[pIdx] = p;
    setStorage(PAYOUTS_KEY, payouts);
    return p;
  },

  retry: async (id) => {
    seedDatabase();
    await mockLatency(150);
    const payouts = getStorage(PAYOUTS_KEY);
    const p = payouts.find(p => p.id === id);
    if (!p) throw makeAxiosError("Payout not found");
    if (p.status !== "failed") {
      throw makeAxiosError("Only failed payouts can be retried");
    }
    const balance = getBalance(p.user_id);
    if (p.amount_vnd > balance) {
      throw makeAxiosError("Insufficient balance to retry");
    }

    const newPayout = {
      id: "pay-" + uuid().substring(0, 8),
      user_id: p.user_id,
      amount_vnd: p.amount_vnd,
      payout_currency: p.payout_currency,
      fx_rate: p.fx_rate,
      fee_vnd: p.fee_vnd,
      net_amount: p.net_amount,
      destination_id: p.destination_id,
      destination_snapshot: p.destination_snapshot,
      status: "pending_approval",
      created_at: nowIso(),
      updated_at: nowIso(),
    };

    payouts.push(newPayout);
    setStorage(PAYOUTS_KEY, payouts);

    const entry = {
      id: "ledg-" + uuid().substring(0, 8),
      user_id: p.user_id,
      type: "withdrawal",
      direction: "debit",
      amount_vnd: p.amount_vnd,
      status: "pending",
      description: `Retry withdrawal → ${p.destination_snapshot.bank_name || ""} (${p.payout_currency})`,
      payout_request_id: newPayout.id,
      created_at: nowIso(),
    };
    appendLedger(entry);

    return newPayout;
  },

  adminPending: async () => {
    seedDatabase();
    await mockLatency(100);
    const payouts = getStorage(PAYOUTS_KEY);
    const pending = payouts.filter(p => p.status === "pending_approval");
    pending.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    return pending;
  },

  adminAll: async () => {
    seedDatabase();
    await mockLatency(120);
    const payouts = getStorage(PAYOUTS_KEY);
    payouts.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    return payouts;
  },

  adminApprove: async (id) => {
    seedDatabase();
    await mockLatency(150);
    const payouts = getStorage(PAYOUTS_KEY);
    const pIdx = payouts.findIndex(p => p.id === id);
    if (pIdx === -1) throw makeAxiosError("Payout not found");
    const p = payouts[pIdx];
    if (p.status !== "pending_approval") {
      throw makeAxiosError("Payout not in pending_approval");
    }

    p.status = "submitted";
    p.rail_reference_id = "P10X-" + uuid().substring(0, 10).toUpperCase();
    const etaHours = Math.floor(Math.random() * 22) + 2;
    p.eta = new Date(Date.now() + etaHours * 3600 * 1000).toISOString();
    p.updated_at = nowIso();

    payouts[pIdx] = p;
    setStorage(PAYOUTS_KEY, payouts);
    return p;
  },

  adminReject: async (id, reason = "Rejected by admin") => {
    seedDatabase();
    await mockLatency(150);
    const payouts = getStorage(PAYOUTS_KEY);
    const pIdx = payouts.findIndex(p => p.id === id);
    if (pIdx === -1) throw makeAxiosError("Payout not found");
    const p = payouts[pIdx];
    if (p.status !== "pending_approval") {
      throw makeAxiosError("Payout not in pending_approval");
    }

    p.status = "rejected";
    p.failure_reason = reason;
    p.updated_at = nowIso();

    const ledger = getStorage(LEDGER_KEY);
    ledger.forEach(entry => {
      if (entry.payout_request_id === p.id && entry.type === "withdrawal") {
        entry.status = "completed";
      }
    });
    setStorage(LEDGER_KEY, ledger);

    const reversalEntry = {
      id: "ledg-" + uuid().substring(0, 8),
      user_id: p.user_id,
      type: "reversal",
      direction: "credit",
      amount_vnd: p.amount_vnd,
      description: `Reversal — payout failed (${reason})`,
      status: "completed",
      payout_request_id: p.id,
      created_at: nowIso(),
    };
    appendLedger(reversalEntry);

    payouts[pIdx] = p;
    setStorage(PAYOUTS_KEY, payouts);
    return p;
  },

  reconciliation: async () => {
    seedDatabase();
    await mockLatency(120);
    const users = getStorage(USERS_KEY).filter(u => u.role === "customer");
    let totalLedger = 0;
    users.forEach(u => {
      totalLedger += getBalance(u.id);
    });

    const payouts = getStorage(PAYOUTS_KEY);
    const completed = payouts.filter(p => p.status === "completed");
    const inflight = payouts.filter(p => ["pending_approval", "approved", "submitted", "processing"].includes(p.status));
    const failed = payouts.filter(p => p.status === "failed");

    const sumVnd = (arr) => arr.reduce((acc, p) => acc + p.amount_vnd, 0);

    return {
      total_ledger_vnd: totalLedger,
      completed_payouts: { count: completed.length, amount_vnd: sumVnd(completed) },
      in_flight_payouts: { count: inflight.length, amount_vnd: sumVnd(inflight) },
      failed_payouts: { count: failed.length, amount_vnd: sumVnd(failed) },
      customer_count: users.length,
    };
  },

  config: async () => {
    seedDatabase();
    await mockLatency(50);
    return CONFIG;
  },

  clients: async () => {
    seedDatabase();
    await mockLatency(120);
    const customers = getStorage(USERS_KEY).filter(u => u.role === "customer");
    const profiles = getStorage(PROFILES_KEY);
    const payouts = getStorage(PAYOUTS_KEY);

    return customers.map(u => {
      const prof = profiles.find(p => p.user_id === u.id) || {};
      const bal = getBalance(u.id);
      const userPayoutsCount = payouts.filter(p => p.user_id === u.id).length;
      return {
        ...u,
        balance_vnd: bal,
        payouts_count: userPayoutsCount,
        kyc_tier: prof.kyc_tier || "basic",
        country: prof.country || "VN",
        onboarded_at: prof.onboarded_at || u.created_at,
      };
    });
  },

  onboardClient: async (body) => {
    seedDatabase();
    await mockLatency(180);
    if (body.starting_balance_vnd < 0) {
      throw makeAxiosError("starting_balance_vnd must be >= 0");
    }
    const users = getStorage(USERS_KEY);
    if (users.some(u => u.email === body.email)) {
      throw makeAxiosError("Email already onboarded");
    }

    const userId = "user-" + uuid().substring(0, 8);
    const user = {
      id: userId,
      name: body.name,
      email: body.email,
      role: "customer",
      created_at: nowIso(),
    };
    users.push(user);
    setStorage(USERS_KEY, users);

    const profiles = getStorage(PROFILES_KEY);
    profiles.push({
      user_id: user.id,
      kyc_tier: body.kyc_tier || "basic",
      country: body.country || "VN",
      note: body.note || null,
      onboarded_at: nowIso(),
    });
    setStorage(PROFILES_KEY, profiles);

    const destId = "dest-" + uuid().substring(0, 8);
    const dest = {
      id: destId,
      user_id: user.id,
      label: "Primary VND Account",
      currency: "VND",
      bank_name: "Vietcombank",
      account_number: "NEW-" + uuid().substring(0, 8).toUpperCase(),
      account_holder: body.name,
      created_at: nowIso(),
    };
    const destinations = getStorage(DESTINATIONS_KEY);
    destinations.push(dest);
    setStorage(DESTINATIONS_KEY, destinations);

    if (body.starting_balance_vnd > 0) {
      const entry = {
        id: "ledg-" + uuid().substring(0, 8),
        user_id: user.id,
        type: "adjustment",
        direction: "credit",
        amount_vnd: body.starting_balance_vnd,
        status: "completed",
        description: `Onboarding credit — KYC ${body.kyc_tier || "basic"}`,
        created_at: nowIso(),
      };
      appendLedger(entry);
    }

    return { user, destination: dest };
  },

  analytics: async () => {
    seedDatabase();
    await mockLatency(120);
    const payouts = getStorage(PAYOUTS_KEY);
    const users = getStorage(USERS_KEY);

    const ccyMap = {};
    payouts.forEach(p => {
      const c = p.payout_currency;
      if (!ccyMap[c]) {
        ccyMap[c] = { count: 0, gross_vnd: 0, fee_vnd: 0 };
      }
      ccyMap[c].count += 1;
      ccyMap[c].gross_vnd += p.amount_vnd;
      ccyMap[c].fee_vnd += p.fee_vnd;
    });

    const totalCount = payouts.length || 1;
    const currency_pairs = Object.keys(ccyMap).map(c => ({
      pair: `VND→${c}`,
      count: ccyMap[c].count,
      gross_vnd: ccyMap[c].gross_vnd,
      fee_vnd: ccyMap[c].fee_vnd,
      share: Math.round((ccyMap[c].count / totalCount) * 10000) / 10000,
    }));

    const statusMap = {};
    payouts.forEach(p => {
      const s = p.status;
      if (!statusMap[s]) {
        statusMap[s] = { count: 0, amount_vnd: 0 };
      }
      statusMap[s].count += 1;
      statusMap[s].amount_vnd += p.amount_vnd;
    });
    const status_breakdown = Object.keys(statusMap).map(s => ({
      status: s,
      count: statusMap[s].count,
      amount_vnd: statusMap[s].amount_vnd,
    }));

    const total_payouts = payouts.length;
    const completed = statusMap["completed"] || { count: 0, amount_vnd: 0 };
    const failed = statusMap["failed"] || { count: 0, amount_vnd: 0 };
    const settled = completed.count + failed.count;
    const success_rate = settled && completed.count ? Math.round((completed.count / settled) * 10000) / 10000 : null;

    const userVolumeMap = {};
    payouts.forEach(p => {
      if (!userVolumeMap[p.user_id]) {
        userVolumeMap[p.user_id] = { gross_vnd: 0, count: 0 };
      }
      userVolumeMap[p.user_id].gross_vnd += p.amount_vnd;
      userVolumeMap[p.user_id].count += 1;
    });

    const userLookup = {};
    users.forEach(u => {
      userLookup[u.id] = u.name;
    });

    const top_customers = Object.keys(userVolumeMap).map(uid => ({
      user_id: uid,
      name: userLookup[uid] || "Unknown",
      gross_vnd: userVolumeMap[uid].gross_vnd,
      count: userVolumeMap[uid].count,
    })).sort((a, b) => b.gross_vnd - a.gross_vnd).slice(0, 5);

    let total_fee_revenue = 0;
    payouts.forEach(p => {
      if (p.status === "completed") {
        total_fee_revenue += p.fee_vnd;
      }
    });

    return {
      total_payouts,
      success_rate,
      total_fee_revenue_vnd: total_fee_revenue,
      currency_pairs,
      status_breakdown,
      top_customers,
    };
  },

  redFlags: async () => {
    seedDatabase();
    await mockLatency(150);
    const HIGH_AMOUNT_VND = 2000000;
    const RAPID_WINDOW_MS = 24 * 3600 * 1000;

    const payouts = getStorage(PAYOUTS_KEY);
    const users = getStorage(USERS_KEY);

    const userLookup = {};
    users.forEach(u => {
      userLookup[u.id] = u.name;
    });

    const flags = [];
    const byUser = {};
    payouts.forEach(p => {
      if (!byUser[p.user_id]) byUser[p.user_id] = [];
      byUser[p.user_id].push(p);
    });

    Object.keys(byUser).forEach(uid => {
      const ps = [...byUser[uid]];
      ps.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

      ps.forEach(p => {
        if (p.amount_vnd >= HIGH_AMOUNT_VND) {
          flags.push({
            id: `flag-${p.id}-high`,
            severity: p.amount_vnd >= HIGH_AMOUNT_VND * 3 ? "high" : "medium",
            rule: "High-value payout",
            user_id: uid,
            user_name: userLookup[uid] || "Unknown",
            payout_id: p.id,
            amount_vnd: p.amount_vnd,
            detail: `Payout of ${p.amount_vnd.toLocaleString()} VND exceeds threshold (${HIGH_AMOUNT_VND.toLocaleString()} VND).`,
            created_at: p.created_at,
          });
        }
      });

      const failedCount = ps.filter(p => p.status === "failed").length;
      if (failedCount >= 2) {
        flags.push({
          id: `flag-${uid}-repeat-fail`,
          severity: "high",
          rule: "Repeat failures",
          user_id: uid,
          user_name: userLookup[uid] || "Unknown",
          payout_id: null,
          amount_vnd: null,
          detail: `${failedCount} failed payouts detected for this customer.`,
          created_at: ps[ps.length - 1].created_at,
        });
      }

      const times = ps.map(p => new Date(p.created_at).getTime());
      for (let i = 0; i < times.length - 2; i++) {
        if (times[i + 2] - times[i] <= RAPID_WINDOW_MS) {
          flags.push({
            id: `flag-${uid}-velocity-${i}`,
            severity: "medium",
            rule: "Velocity spike",
            user_id: uid,
            user_name: userLookup[uid] || "Unknown",
            payout_id: ps[i + 2].id,
            amount_vnd: ps[i + 2].amount_vnd,
            detail: "3+ payouts within 24 hours.",
            created_at: ps[i + 2].created_at,
          });
          break;
        }
      }

      ps.forEach(p => {
        if (p.payout_currency === "USD" && p.amount_vnd >= 1000000) {
          flags.push({
            id: `flag-${p.id}-xborder`,
            severity: "low",
            rule: "Cross-border USD",
            user_id: uid,
            user_name: userLookup[uid] || "Unknown",
            payout_id: p.id,
            amount_vnd: p.amount_vnd,
            detail: "Large USD cross-border payout — enhanced review recommended.",
            created_at: p.created_at,
          });
        }
      });
    });

    const severityOrder = { high: 0, medium: 1, low: 2 };
    flags.sort((a, b) => {
      if (severityOrder[a.severity] !== severityOrder[b.severity]) {
        return severityOrder[a.severity] - severityOrder[b.severity];
      }
      return new Date(b.created_at) - new Date(a.created_at);
    });

    const counts = { high: 0, medium: 0, low: 0 };
    flags.forEach(f => {
      counts[f.severity] += 1;
    });

    return {
      flags: flags.slice(0, 100),
      counts,
      total: flags.length,
    };
  },

  risk: async () => {
    seedDatabase();
    await mockLatency(120);
    const customers = getStorage(USERS_KEY).filter(u => u.role === "customer");
    const payouts = getStorage(PAYOUTS_KEY);

    let totalLedger = 0;
    const balances = customers.map(u => {
      const b = getBalance(u.id);
      totalLedger += b;
      return { name: u.name, balance: b };
    });
    balances.sort((a, b) => b.balance - a.balance);

    const inflight = payouts.filter(p => ["pending_approval", "approved", "submitted", "processing"].includes(p.status));
    const exposure = inflight.reduce((acc, p) => acc + p.amount_vnd, 0);

    const xb = payouts.filter(p => p.payout_currency === "USD");
    const xbAmt = xb.reduce((acc, p) => acc + p.amount_vnd, 0);
    const xbN = xb.length;

    const allPayouts = payouts.length;
    const failedRejected = payouts.filter(p => ["failed", "rejected"].includes(p.status)).length;
    const reversalRatio = allPayouts ? Math.round((failedRejected / allPayouts) * 10000) / 10000 : 0;

    const topConc = totalLedger && balances.length ? Math.round((balances[0].balance / totalLedger) * 10000) / 10000 : 0;
    const topName = balances.length ? balances[0].name : "—";

    const riskScore = Math.min(
      100,
      Math.round(reversalRatio * 100 * 2 + topConc * 100 * 0.5 + (xbAmt > 5000000 ? 30 : 10))
    );

    let riskBand = "low";
    if (riskScore >= 60) {
      riskBand = "elevated";
    } else if (riskScore >= 25) {
      riskBand = "moderate";
    }

    return {
      total_exposure_vnd: exposure,
      cross_border_exposure_vnd: xbAmt,
      cross_border_count: xbN,
      reversal_ratio: reversalRatio,
      concentration_top_share: topConc,
      concentration_top_name: topName,
      risk_score: riskScore,
      risk_band: riskBand,
      total_ledger_vnd: totalLedger,
    };
  },

  thunesAccount: async () => {
    seedDatabase();
    await mockLatency(120);
    const THUNES_USD_PREFUND = 50000.00;
    const THUNES_USD_LOW_THRESHOLD = 10000.00;

    const payouts = getStorage(PAYOUTS_KEY);
    const completedUsd = payouts.filter(p => p.payout_currency === "USD" && p.status === "completed");
    completedUsd.sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at));

    const debitedUsd = completedUsd.reduce((acc, p) => acc + parseFloat(p.net_amount || 0), 0);

    const inflightUsd = payouts.filter(p => p.payout_currency === "USD" && ["pending_approval", "approved", "submitted", "processing"].includes(p.status));
    const heldUsd = inflightUsd.reduce((acc, p) => acc + parseFloat(p.net_amount || 0), 0);

    const availableUsd = THUNES_USD_PREFUND - debitedUsd - heldUsd;

    const transactions = [
      {
        id: "fund-init",
        type: "prefund",
        amount_usd: THUNES_USD_PREFUND,
        direction: "credit",
        description: "Initial prefund wire — Pay10x Treasury → Thunes nostro",
        reference: "SWIFT-INIT-0001",
        created_at: "2026-01-02T09:00:00+00:00",
      }
    ];

    completedUsd.forEach(p => {
      transactions.push({
        id: `debit-${p.id}`,
        type: "payout_debit",
        amount_usd: parseFloat(p.net_amount),
        direction: "debit",
        description: `Payout settled — ${(p.destination_snapshot && p.destination_snapshot.bank_name) || "destination"}`,
        reference: p.rail_reference_id,
        created_at: p.updated_at || p.created_at,
      });
    });

    transactions.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    return {
      provider: "Thunes",
      account_type: "USD nostro / prefund",
      account_reference: "THUNES-NOSTRO-USD-8842",
      currency: "USD",
      prefund_usd: THUNES_USD_PREFUND,
      debited_usd: Math.round(debitedUsd * 100) / 100,
      held_usd: Math.round(heldUsd * 100) / 100,
      available_usd: Math.round(availableUsd * 100) / 100,
      low_threshold_usd: THUNES_USD_LOW_THRESHOLD,
      is_low: availableUsd < THUNES_USD_LOW_THRESHOLD,
      utilisation_pct: THUNES_USD_PREFUND ? Math.round(((debitedUsd + heldUsd) / THUNES_USD_PREFUND) * 10000) / 100 : 0,
      transactions: transactions.slice(0, 50),
      inflight_count: inflightUsd.length,
      completed_count: completedUsd.length,
    };
  },
};
